import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    tokens,
} from '@fluentui/react-components'
import { CalendarDayRegular, CalendarMonthRegular, CalendarWorkWeekRegular } from '@fluentui/react-icons'
import useModules from '../../hooks/module/useModules'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate } from '../BaseOverview/BaseOverviewFilter'
import DateRange from '../../components/DatePicker/DateRange'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import FilePicker from '../../components/TagPicker/FilePicker'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import { DateTime } from 'luxon'
import { DisbursementOverviewDto } from '../../config/registrationConfig'
import { MoneyDto } from '../../config/i18nConfig'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import DisbursementListItem from './DisbursementListItem'
import DisbursementHeaderItem from './DisbursementHeaderItem'

export const useItemStyles = makeStyles({
    listDisbursementItem: {
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewPriceWidth) var(--baseOverviewPriceWidth)',
    },
    listDisbursementItemSelectable: {
        gridTemplateColumns:
            'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) var(--baseOverviewDateWidth) auto min-content var(--baseOverviewPriceWidth) min-content',
    },
    listDisbursementItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listDisbursementItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewDateWidth) auto',
        gridTemplateRows: 'auto 1fr',
    },
    listDisbursementItemSmallBottomRow: {
        display: 'grid',
        gridTemplateColumns: 'auto var(--baseOverviewPriceWidth) min-content',
        gap: tokens.spacingVerticalS,
    },
    listDisbursementItemOverflow: {
        overflowWrap: 'anywhere',
    },
    turnoverField: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingVerticalS,
    },
})

type BaseItem = DisbursementOverviewDto

export type OverviewItem = {
    // Is calculated during runtime and not returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
} & BaseItem

export type DisbursementOverviewProps = BaseOverviewProps<
    ExtendedDisbursementOverviewDto,
    DisbursementFilterType,
    DisbursementSortingType,
    DisbursementOverviewCount
>

export type BaseDisbursementOverviewProps = Partial<DisbursementOverviewProps> & {
    baseUrl: string

    fileId?: string
    hideFileField?: boolean
    hideInvoiceField?: boolean
    renderLink?: boolean
}

export type BaseDisbursementLineOverviewProps = BaseDisbursementOverviewProps &
    BaseOverviewLineItemProps<ExtendedDisbursementOverviewDto, DisbursementFilterType, DisbursementSortingType>

export type DisbursementOverviewCount = BaseOverviewCount & {
    currencies: DisbursementCurrencyOverviewCount[]
}

export type DisbursementCurrencyOverviewCount = {
    currency: string
    distance: number
    turnover: string
    invoiceTurnover: string
}

export type ExtendedDisbursementOverviewDto = DisbursementOverviewDto & {
    // Is calculated during runtime and not actually returned by the API. Expressed in American number notation (0.00).
    turnover: MoneyDto
    invoiceTurnover: MoneyDto
}

export type DisbursementFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        users?: string[]
        isBilled?: boolean
        files?: string[]
        fileOwners?: string[]
        invoiceApprovals?: string[]
    }

export type DisbursementSorting = {
    id: string
    date: string
    description: string
    'file.name': string
    'file.reference': string
}

export type DisbursementSortingType = BaseOverviewSortingType<DisbursementSorting>

type BaseTaskOverviewFilterProps = BaseDisbursementOverviewProps &
    BaseOverviewFilterProps<ExtendedDisbursementOverviewDto, DisbursementFilterType, DisbursementSortingType>

function DisbursementFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<DisbursementFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('registration.filter.user')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.users || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('users', selectedOptions)}
                />
            </Field>
            {modules.has(ModuleType.Invoices) && (
                <Field label={translate('registration.filter.isBilled')} orientation={orientation}>
                    <BooleanCombobox
                        selectedOptions={[values.isBilled?.toString() || 'mixed']}
                        onOptionSelect={(_, data: CheckboxOnChangeData) =>
                            updateFieldValue('isBilled', data.checked === 'mixed' ? undefined : data.checked)
                        }
                    />
                </Field>
            )}
            {!props.hideFileField && (
                <Field label={translate('registration.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('registration.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}
const filterTagItems: DisbursementFilterType[] = [
    {
        from: DateTime.local().toISODate(),
        to: DateTime.local().toISODate(),
        tag: 'day',
        icon: <CalendarDayRegular />,
        title: 'disbursement.filter.today',
    },
    {
        from: DateTime.local().startOf('week').toISODate(),
        to: DateTime.local().endOf('week').toISODate(),
        tag: 'week',
        icon: <CalendarWorkWeekRegular />,
        title: 'disbursement.filter.week',
    },
    {
        from: DateTime.local().startOf('month').toISODate(),
        to: DateTime.local().endOf('month').toISODate(),
        tag: 'month',
        icon: <CalendarMonthRegular />,
        title: 'disbursement.filter.month',
    },
]

const headerSortingItems: DisbursementSortingType[] = [
    { property: ['date', 'id'], direction: SortDirection.Ascending },
    { property: ['date', 'id'], direction: SortDirection.Descending, default: true },
    { property: ['description', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.descriptionaz' },
    { property: ['description', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.descriptionza' },
    { property: ['file.name', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.fileaz' },
    { property: ['file.name', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.fileza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: DisbursementOverviewProps) {
    const { values, setValues } = useFormikContext<DisbursementFilterType>()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    return (
        <div className={overviewStyles.flexGap}>
            {filterTagItems.map((tag, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === tag.tag}
                    onClick={() =>
                        setValues({
                            ...props.defaultFilterValues,
                            ...tag,
                        })
                    }
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <InteractionTagPrimary icon={tag.icon}> {translate(tag.title)}</InteractionTagPrimary>
                </InteractionTag>
            ))}
        </div>
    )
}

export default function BaseDisbursementOverview(props: BaseDisbursementOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const defaultFilterValues = useMemo<DisbursementFilterType>(
        () => ({
            from: '',
            to: '',
            users: [],
            attributes: [],
            query: '',
            tag: 'day',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = (column?: number, filterValues?: DisbursementFilterType): Promise<DisbursementOverviewCount> => {
        return axios.get('/disbursement/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: DisbursementFilterType,
        sortingValues?: DisbursementSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedDisbursementOverviewDto[]> => {
        return axios
            .get('/disbursement', {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
            })
            .then((response: AxiosResponse<OverviewItem[]>): ExtendedDisbursementOverviewDto[] =>
                (response?.data || []).map((item: OverviewItem) => ({
                    ...item,
                    turnover: item.rate,
                    invoiceTurnover: item.price && {
                        amount: item.price.subtotal,
                        currency: item.price.currency,
                    },
                })),
            )
    }

    return (
        <BaseOverview<ExtendedDisbursementOverviewDto, DisbursementFilterType, DisbursementSortingType, DisbursementOverviewCount>
            defaultFilterValues={defaultFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderContent={DisbursementHeaderItem}
            listItemContent={(props: BaseDisbursementLineOverviewProps) => <DisbursementListItem {...props} />}
            listItemClassnames={() => [
                props.onItemSelected ? itemStyles.listDisbursementItemSelectable : itemStyles.listDisbursementItem,
                listItemSmall && (props.onItemSelected ? itemStyles.listDisbursementItemSmallSelectable : itemStyles.listDisbursementItemSmall),
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            filterContent={DisbursementFilter}
            filterFromJson={values => ({
                ...values,
                from: values.tag ? generateFromDate(values.tag) : values.from,
                to: values.tag ? generateToDate(values.tag) : values.to,
            })}
            filterToJson={values => ({
                ...values,
                from: values.tag ? '' : values.from,
                to: values.tag ? '' : values.to,
                icon: undefined,
                title: undefined,
            })}
            inputPlaceholderKey={'disbursement.filter.search'}
            {...props}
        />
    )
}
